import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// UI-label-only translations. Long-form copy (item descriptions, artist bios)
// remains in whatever language was entered in the admin — those will become
// bilingual via per-item fields in a later iteration.
const DICTIONARY = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.gallery': 'Gallery',
    'nav.artists': 'Artists',
    'nav.annex': 'Antiques & Acquisitions',
    'nav.about': 'About',
    'nav.contact': 'Contact',

    // Footer
    'footer.tagline': 'Contemporary Art and Fine Antiques.',
    'footer.quickLinks': 'Quick Links',
    'footer.about': 'About Us',
    'footer.copyright': '© 2024 The Chimera Gallery. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.cookies': 'Cookie Policy',

    // Common
    'common.inquire': 'Inquire',
    'common.inquireSubject': 'Enquiry about',
    'common.requestInformation': 'Request Information',
    'common.search': 'Search by title or artist...',
    'common.noResults': 'No artworks found matching your search.',
    'common.all': 'All',
    'common.viewWorks': 'View Works',

    // Gallery page
    'gallery.title': 'Contemporary Art & Acquisitions',
    'gallery.subtitle': 'Explore our exquisite collection of Art masterpieces',
    'gallery.filterPainting': 'Painting',
    'gallery.filterSculpture': 'Sculpture',
    'gallery.filterMixedMedia': 'Mixed Media',
    'gallery.filterAntique': 'Antique',
    'gallery.filterDrawing': 'Drawing',

    // Artists page
    'artists.title': 'Our Artists',
    'artists.subtitle': 'Meet the contemporary voices represented by The Chimera Gallery',
    'artists.worksBy': 'Works by',
    'artists.inquireArtist': 'Inquire about this artist',
    'artists.backToArtists': '← Back to Artists',
    'artists.worksAvailable': 'works available',
    'artists.workAvailable': 'work available',

    // Annex (Antiques & Acquisitions)
    'annex.title': 'Antiques & Acquisitions',
    'annex.subtitle': 'A curated annex of antique and design objects.',
    'annex.tabAntique': 'Antiques',
    'annex.tabAcquisition': 'Acquisitions',

    // About
    'about.title': 'About The Chimera Gallery',

    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': "We'd love to hear from you.",
    'contact.name': 'Name',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.subject': 'Subject',
    'contact.message': 'Message',
    'contact.submit': 'Send Message',
    'contact.sending': 'Sending…',
    'contact.success': 'Message sent — thank you, we will be in touch shortly.',
    'contact.error': 'Sorry, something went wrong. Please email us directly.',

    // Home
    'home.heroCta': 'Explore the Gallery',
    'home.heroCtaSecondary': 'Visit the Annex',
    'home.heroTagline': 'Contemporary Art and Fine Antiques',
    'home.heroLocations': 'France and Ireland',
    'home.exploreGallery': 'Explore Gallery',
    'home.featured': 'Featured Collection',
    'home.featuredSubtitle': 'Discover our carefully curated selection of art and antiques',
    'home.viewFullGallery': 'View Full Gallery',
    'home.viewDetails': 'View Details',
    'home.locations': 'Our Locations',
    'home.testimonials': 'What Our Clients Say',
    'home.exploreAll': 'Explore the full collection',

    // Newsletter
    'newsletter.eyebrow': 'The Chimera Circle',
    'newsletter.title': 'New Acquisitions, First',
    'newsletter.body': 'Once a month, a short letter with the pieces we\'ve just brought in — painters, sculptors, antiques. No noise, no marketing. Written by hand.',
    'newsletter.placeholder': 'your@email.com',
    'newsletter.cta': 'Subscribe',
    'newsletter.subscribing': 'Subscribing…',
    'newsletter.success': 'Welcome — you\'re on the list.',
    'newsletter.already': 'You\'re already subscribed — thank you.',
    'newsletter.error': 'Sorry, something went wrong. Please try again.',
    'newsletter.privacy': 'We keep your email to ourselves. Unsubscribe any time.',

    // Instagram
    'instagram.title': 'From Our Instagram',
    'instagram.subtitle': 'A glimpse behind the scenes — new arrivals, the annex and life at the gallery.',
    'instagram.follow': 'Follow @chimera_part_2',

    // Promo video
    'home.watchFilm': 'Watch the Film',

    // Share
    'share.label': 'Share',
    'share.copied': 'Link copied',
    'share.copy': 'Copy link',
    'share.email': 'Email',

    // Language switcher (always shown in both languages)
    'lang.en': 'EN',
    'lang.fr': 'FR',
  },

  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.gallery': 'Galerie',
    'nav.artists': 'Artistes',
    'nav.annex': 'Antiquités & Acquisitions',
    'nav.about': 'À propos',
    'nav.contact': 'Contact',

    // Footer
    'footer.tagline': 'Art Contemporain et Belles Antiquités.',
    'footer.quickLinks': 'Liens rapides',
    'footer.about': 'À propos',
    'footer.copyright': '© 2024 The Chimera Gallery. Tous droits réservés.',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': "Conditions d'utilisation",
    'footer.cookies': 'Politique relative aux cookies',

    // Common
    'common.inquire': 'Renseignements',
    'common.inquireSubject': 'Renseignement à propos de',
    'common.requestInformation': 'Demander des informations',
    'common.search': 'Rechercher par titre ou artiste…',
    'common.noResults': 'Aucune œuvre ne correspond à votre recherche.',
    'common.all': 'Toutes',
    'common.viewWorks': 'Voir les œuvres',

    // Gallery page
    'gallery.title': 'Art Contemporain & Acquisitions',
    'gallery.subtitle': "Explorez notre collection raffinée de chefs-d'œuvre",
    'gallery.filterPainting': 'Peinture',
    'gallery.filterSculpture': 'Sculpture',
    'gallery.filterMixedMedia': 'Techniques mixtes',
    'gallery.filterAntique': 'Antiquité',
    'gallery.filterDrawing': 'Dessin',

    // Artists page
    'artists.title': 'Nos Artistes',
    'artists.subtitle': "Découvrez les voix contemporaines représentées par The Chimera Gallery",
    'artists.worksBy': 'Œuvres de',
    'artists.inquireArtist': 'Se renseigner sur cet artiste',
    'artists.backToArtists': '← Retour aux artistes',
    'artists.worksAvailable': 'œuvres disponibles',
    'artists.workAvailable': 'œuvre disponible',

    // Annex
    'annex.title': 'Antiquités & Acquisitions',
    'annex.subtitle': "Une annexe choisie d'objets d'antiquité et de design.",
    'annex.tabAntique': 'Antiquités',
    'annex.tabAcquisition': 'Acquisitions',

    // About
    'about.title': 'À propos de The Chimera Gallery',

    // Contact
    'contact.title': 'Contactez-nous',
    'contact.subtitle': "Nous serions ravis d'avoir de vos nouvelles.",
    'contact.name': 'Nom',
    'contact.email': 'Email',
    'contact.phone': 'Téléphone',
    'contact.subject': 'Sujet',
    'contact.message': 'Message',
    'contact.submit': 'Envoyer le message',
    'contact.sending': 'Envoi…',
    'contact.success': 'Message envoyé — merci, nous vous recontacterons bientôt.',
    'contact.error': "Désolé, une erreur s'est produite. Merci de nous écrire directement.",

    // Home
    'home.heroCta': 'Découvrir la galerie',
    'home.heroCtaSecondary': "Visiter l'annexe",
    'home.heroTagline': 'Art Contemporain et Belles Antiquités',
    'home.heroLocations': 'France et Irlande',
    'home.exploreGallery': 'Explorer la Galerie',
    'home.featured': 'Œuvres en vedette',
    'home.featuredSubtitle': 'Découvrez notre sélection raffinée d\'art et d\'antiquités',
    'home.viewFullGallery': 'Voir toute la galerie',
    'home.viewDetails': 'Voir les détails',
    'home.locations': 'Nos Adresses',
    'home.testimonials': 'Témoignages de nos clients',
    'home.exploreAll': 'Explorer toute la collection',

    // Newsletter
    'newsletter.eyebrow': 'Le Cercle Chimera',
    'newsletter.title': 'Nouvelles Acquisitions, en Avant-Première',
    'newsletter.body': 'Une fois par mois, une courte lettre avec les pièces que nous venons de recevoir — peintres, sculpteurs, antiquités. Pas de bruit, pas de marketing. Écrite à la main.',
    'newsletter.placeholder': 'votre@email.com',
    'newsletter.cta': 'S\'abonner',
    'newsletter.subscribing': 'Abonnement…',
    'newsletter.success': 'Bienvenue — vous êtes sur la liste.',
    'newsletter.already': 'Vous êtes déjà abonné(e) — merci.',
    'newsletter.error': 'Désolé, une erreur est survenue. Réessayez.',
    'newsletter.privacy': 'Nous gardons votre email pour nous. Désabonnement à tout moment.',

    // Instagram
    'instagram.title': 'Sur Notre Instagram',
    'instagram.subtitle': "Un aperçu des coulisses — nouvelles arrivées, l'annexe et la vie de la galerie.",
    'instagram.follow': 'Suivre @chimera_part_2',

    // Promo video
    'home.watchFilm': 'Voir le Film',

    // Share
    'share.label': 'Partager',
    'share.copied': 'Lien copié',
    'share.copy': 'Copier le lien',
    'share.email': 'Email',

    'lang.en': 'EN',
    'lang.fr': 'FR',
  },
};

const STORAGE_KEY = 'chimera.lang';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'fr') return saved;
    // Fall back to browser preference, default English
    const browser = (window.navigator.language || '').toLowerCase();
    return browser.startsWith('fr') ? 'fr' : 'en';
  });

  const setLang = useCallback((next) => {
    if (next !== 'en' && next !== 'fr') return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key) => {
    const dict = DICTIONARY[lang] || DICTIONARY.en;
    return dict[key] ?? DICTIONARY.en[key] ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useT = () => useContext(LanguageContext);

// Pick the language-appropriate value for a content field.
// Falls back to the English/base field when the FR variant is missing.
export const localize = (obj, field, lang) => {
  if (!obj) return '';
  if (lang === 'fr') return (obj[`${field}_fr`] || '').trim() || obj[field] || '';
  return obj[field] || '';
};
